import { useState } from "react";
import Select from "react-select";
import ButtonLoader from "./ButtonLoader";

const getDaysInMonth = (month, year) => {
  if (!month || !year) return 31;
  return new Date(year.value, month.value, 0).getDate();
};

const buildDayOptions = (month, year) =>
  Array.from({ length: getDaysInMonth(month, year) }, (_, i) => ({ value: i + 1, label: i + 1 }));

const months = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

const generateBirthYears = () => {
  const currentYear = new Date().getFullYear();
  return Array.from({ length: (currentYear - 18) - (currentYear - 66) + 1 }, (_, i) => {
    const year = (currentYear - 66) + i;
    return { value: year, label: year };
  });
};

const generateEmploymentYears = () => {
  const currentYear = new Date().getFullYear();
  return Array.from({ length: currentYear + 5 - 2000 + 1 }, (_, i) => {
    const year = 2000 + i;
    return { value: year, label: year };
  });
};

const birthYears = generateBirthYears();
const employmentYears = generateEmploymentYears();

const dateToSelects = (date) => {
  if (!date) return { day: null, month: null, year: null };
  return {
    day: { value: date.getDate(), label: date.getDate() },
    month: { value: date.getMonth() + 1, label: months[date.getMonth()].label },
    year: { value: date.getFullYear(), label: date.getFullYear() },
  };
};

const LabourerForm = ({
  initialFullName = '',
  initialEmail = '',
  initialDateOfBirth = null,
  initialOnboardingDate = null,
  initialOffboardingDate = null,
  isLoading,
  onSubmit,
}) => {
  const [fullName, setFullName] = useState(initialFullName);
  const [email, setEmail] = useState(initialEmail);

  const dobInit = dateToSelects(initialDateOfBirth);
  const [dobDay, setDobDay] = useState(dobInit.day);
  const [dobMonth, setDobMonth] = useState(dobInit.month);
  const [dobYear, setDobYear] = useState(dobInit.year);

  const onboardingInit = dateToSelects(initialOnboardingDate);
  const [onboardingDay, setOnboardingDay] = useState(onboardingInit.day);
  const [onboardingMonth, setOnboardingMonth] = useState(onboardingInit.month);
  const [onboardingYear, setOnboardingYear] = useState(onboardingInit.year);

  const offboardingInit = dateToSelects(initialOffboardingDate);
  const [offboardingDay, setOffboardingDay] = useState(offboardingInit.day);
  const [offboardingMonth, setOffboardingMonth] = useState(offboardingInit.month);
  const [offboardingYear, setOffboardingYear] = useState(offboardingInit.year);

  const clampDay = (day, setDay, month, year) => {
    if (!day || !month || !year) return;
    if (day.value > getDaysInMonth(month, year)) setDay(null);
  };

  const handleDobMonthChange = (option) => { setDobMonth(option); clampDay(dobDay, setDobDay, option, dobYear); };
  const handleDobYearChange = (option) => { setDobYear(option); clampDay(dobDay, setDobDay, dobMonth, option); };

  const handleOnboardingMonthChange = (option) => { setOnboardingMonth(option); clampDay(onboardingDay, setOnboardingDay, option, onboardingYear); };
  const handleOnboardingYearChange = (option) => { setOnboardingYear(option); clampDay(onboardingDay, setOnboardingDay, onboardingMonth, option); };

  const handleOffboardingMonthChange = (option) => { setOffboardingMonth(option); clampDay(offboardingDay, setOffboardingDay, option, offboardingYear); };
  const handleOffboardingYearChange = (option) => { setOffboardingYear(option); clampDay(offboardingDay, setOffboardingDay, offboardingMonth, option); };

  const handleSubmit = (e) => {
    e.preventDefault();
    const pad = (n) => String(n).padStart(2, '0');
    const buildDateString = (d, m, y) => {
      if (!d || !m || !y) return null;
      return `${y.value}-${pad(m.value)}-${pad(d.value)}`;
    };

    onSubmit({
      fullName,
      email,
      dateOfBirth: buildDateString(dobDay, dobMonth, dobYear),
      onboardingDate: buildDateString(onboardingDay, onboardingMonth, onboardingYear),
      offboardingDate: buildDateString(offboardingDay, offboardingMonth, offboardingYear),
    });
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col w-full overflow-visible">
      <div className="p-4 flex flex-col gap-4">
        <label className="flex flex-col gap-1 w-full">
          Full name
          <input
            type="text"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
          />
        </label>

        <label className="flex flex-col gap-1 w-full">
          Email
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </label>

        <div className="flex flex-col gap-1">
          <label>Date of birth</label>
          <div className="grid md:grid-cols-3 gap-2 items-center">
            <Select options={buildDayOptions(dobMonth, dobYear)} placeholder="Select day" value={dobDay} onChange={setDobDay} />
            <Select options={months} placeholder="Select month" value={dobMonth} onChange={handleDobMonthChange} />
            <Select options={birthYears} placeholder="Select year" value={dobYear} onChange={handleDobYearChange} />
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <label>Onboarding date</label>
          <div className="grid md:grid-cols-3 gap-2 items-center">
            <Select options={buildDayOptions(onboardingMonth, onboardingYear)} placeholder="Select day" value={onboardingDay} onChange={setOnboardingDay} />
            <Select options={months} placeholder="Select month" value={onboardingMonth} onChange={handleOnboardingMonthChange} />
            <Select options={employmentYears} placeholder="Select year" value={onboardingYear} onChange={handleOnboardingYearChange} />
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <label>Offboarding date</label>
          <div className="grid md:grid-cols-3 gap-2 items-center">
            <Select options={buildDayOptions(offboardingMonth, offboardingYear)} placeholder="Select day" value={offboardingDay} onChange={setOffboardingDay} />
            <Select options={months} placeholder="Select month" value={offboardingMonth} onChange={handleOffboardingMonthChange} />
            <Select options={employmentYears} placeholder="Select year" value={offboardingYear} onChange={handleOffboardingYearChange} />
          </div>
        </div>

        <button
          type="submit"
          className="ase-button-primary h-[48px] w-full flex items-center justify-center"
        >
          {isLoading ? <ButtonLoader /> : 'Submit'}
        </button>
      </div>
    </form>
  );
};

export default LabourerForm;
